import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Tab,
  Tabs,
  Spinner,
  Alert,
  Form,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faFile, faFileAlt } from "@fortawesome/free-solid-svg-icons";
import { formatDate } from "../../utils/dateUtils";
import { decodeSpecialCharacters } from "../../utils/textUtils";
import MeetSyncSection from '../MeetSyncSection';

const EventDetailModal = ({
  show,
  onHide,
  selectedEvent,
  activeTab,
  onTabSelect,
  validatingGuests,
  generatingAttendance,
  validationResult,
  attendanceQR,
  attendanceLink,
  loadingReports,
  error,
  htmlContent,
  onViewEventReport,
  onDownloadEventReport,
  onDownloadQR,
  onCopyLink,
  hideReportsTab = false,
  hideInfoTab = false,
  // Meet sync props
  meetStatus,
  loadingMeetStatus,
  syncingMeet,
  meetSyncResult,
  meetError,
  onSyncMeet,
  onResyncMeet,
  onRefreshMeetStatus,
}) => {
  const [copyButtonText, setCopyButtonText] = useState('Copiar');
  const [copyButtonDisabled, setCopyButtonDisabled] = useState(false);
  const [qrButtonText, setQrButtonText] = useState('Descargar QR');
  const [qrButtonDisabled, setQrButtonDisabled] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState(null);

  const handleCopyLink = async (url) => {
    setCopyButtonText('Copiado');
    setCopyButtonDisabled(true);
    
    await onCopyLink(url);
    
    setTimeout(() => {
      setCopyButtonText('Copiar');
      setCopyButtonDisabled(false);
    }, 2000);
  };

  const handleDownloadQR = async (qrCodeImage, eventTitle) => {
    setQrButtonText('Descargado');
    setQrButtonDisabled(true);
    
    await onDownloadQR(qrCodeImage, eventTitle);
    
    setTimeout(() => {
      setQrButtonText('Descargar QR');
      setQrButtonDisabled(false);
    }, 2000);
  };

  const confirmDownload = (soloAsistentes = true) => {
    if (soloAsistentes) {
      onDownloadEventReport(downloadFormat);
    } else {
      // Llamar función personalizada para todos los invitados
      if (onDownloadEventReport.custom) {
        onDownloadEventReport.custom(downloadFormat, false);
      }
    }
    
    setShowDownloadModal(false);
    setDownloadFormat(null);
  };

  useEffect(() => {
    if (!show) {
      setCopyButtonText('Copiar');
      setCopyButtonDisabled(false);
      setQrButtonText('Descargar QR');
      setQrButtonDisabled(false);
      setShowDownloadModal(false);
      setDownloadFormat(null);
    }
  }, [show]);
  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Detalles del Evento</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedEvent && (
          <Tabs activeKey={activeTab} onSelect={onTabSelect} className="mb-3">
            {!hideInfoTab && (
              <Tab eventKey="info" title="Información del Evento">
              <div className="pt-3">
                <h5>
                  {decodeSpecialCharacters(
                    selectedEvent.title ||
                      selectedEvent.summary ||
                      "Evento sin título"
                  )}
                </h5>
                <div className="row">
                  <div className="col-md-6">
                    <p>
                      <strong>Fecha de inicio:</strong>
                      <br />
                      {selectedEvent.start ? formatDate(selectedEvent.start) : 'Fecha no disponible'}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <p>
                      <strong>Fecha de fin:</strong>
                      <br />
                      {selectedEvent.end ? formatDate(selectedEvent.end) : 'Fecha no disponible'}
                    </p>
                  </div>
                </div>
                {selectedEvent.resource?.location && (
                  <p>
                    <strong>Ubicación:</strong>{" "}
                    {decodeSpecialCharacters(selectedEvent.resource.location)}
                  </p>
                )}
                {selectedEvent.resource?.description && (
                  <div>
                    <p>
                      <strong>Descripción:</strong>
                    </p>
                    <p style={{ whiteSpace: "pre-line" }}>
                      {decodeSpecialCharacters(
                        selectedEvent.resource.description
                      )}
                    </p>
                  </div>
                )}
              </div>
            </Tab>
            )}

            <Tab eventKey="attendance" title="Registro de Asistencia">
              <div className="pt-3">
                {/* Mostrar mensaje de carga general si está cargando Meet Y validando */}
                {loadingMeetStatus && validatingGuests && (
                  <div className="text-center py-4 mb-3">
                    <Spinner animation="border" className="me-2" />
                    <div className="mt-2">
                      <strong>Cargando información del evento...</strong>
                    </div>
                  </div>
                )}

                {/* Sección de sincronización de Meet - solo mostrar si no está en carga inicial */}
                {!(loadingMeetStatus && validatingGuests) && (
                  <MeetSyncSection
                    meetStatus={meetStatus}
                    loadingStatus={loadingMeetStatus}
                    syncing={syncingMeet}
                    syncResult={meetSyncResult}
                    error={meetError}
                    onSync={onSyncMeet}
                    onResync={onResyncMeet}
                    eventDbId={selectedEvent?.db_id || selectedEvent?.id}
                    onRefreshStatus={onRefreshMeetStatus}
                  />
                )}

                {/* Mostrar validación solo si no está en carga inicial */}
                {!(loadingMeetStatus && validatingGuests) && validatingGuests && (
                  <div className="text-center mb-3">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Validando invitados...
                  </div>
                )}

                {generatingAttendance && (
                  <div className="text-center mb-3">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Generando credenciales de asistencia...
                  </div>
                )}

                {error && error.includes("invitados") && (
                  <Alert variant="warning" className="mb-3">
                    <h6>
                      Este evento no tiene invitados registrados en Google
                      Calendar.
                    </h6>
                  </Alert>
                )}

                {error && !error.includes("invitados") && (
                  <Alert variant="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                {validationResult && (
                  <Alert variant="success" className="mb-3">
                    <h6>Invitados validados correctamente</h6>
                    <p>
                      Se procesaron {validationResult.totalInvitados} invitados
                      para este evento.
                    </p>
                  </Alert>
                )}

                {/* Alerta informativa para usuarios externos */}
                {(attendanceQR || attendanceLink) && (
                  <Alert variant="info" className="mb-3">
                    <small>
                      <strong>Importante:</strong> Los usuarios externos deben registrar su asistencia usando el código QR o el link proporcionado.
                    </small>
                  </Alert>
                )}

                {attendanceQR && (
                  <div className="mb-4">
                    <h6>Código QR:</h6>
                    <div className="text-center mb-3">
                      <img
                        src={attendanceQR.qrCodeImage}
                        alt="Código QR de Asistencia"
                        style={{ maxWidth: "200px", width: "100%" }}
                        className="border rounded"
                      />
                    </div>
                    <div className="text-center">
                      <Button
                        variant="outline-primary"
                        onClick={() =>
                          handleDownloadQR(
                            attendanceQR.qrCodeImage,
                            attendanceQR.eventTitle
                          )
                        }
                        disabled={qrButtonDisabled}
                      >
                        {qrButtonText}
                      </Button>
                    </div>
                  </div>
                )}

                {attendanceLink && (
                  <div className="mb-4">
                    <h6>Link de Asistencia:</h6>
                    <div className="input-group mb-2">
                      <Form.Control
                        type="text"
                        value={attendanceLink.url}
                        readOnly
                        className="bg-light"
                      />
                      <Button
                        variant="outline-primary"
                        onClick={() => handleCopyLink(attendanceLink.url)}
                        disabled={copyButtonDisabled}
                      >
                        {copyButtonText}
                      </Button>
                    </div>
                  </div>
                )}

                {!validatingGuests &&
                  !generatingAttendance &&
                  !validationResult &&
                  !attendanceQR &&
                  !attendanceLink &&
                  !error &&
                  !loadingMeetStatus &&
                  !selectedEvent && (
                    <Alert variant="info" className="mb-3">
                      <h6>Registro de Asistencia</h6>
                      <p>
                        Selecciona un evento para generar códigos QR y links de
                        asistencia.
                      </p>
                    </Alert>
                  )}
              </div>
            </Tab>

            {!hideReportsTab && (
              <Tab eventKey="reports" title="Reportes de Asistencia">
                <div className="pt-3">
                  {loadingReports && (
                    <div className="text-center mb-3">
                      <Spinner animation="border" size="sm" className="me-2" />
                      Cargando reportes...
                    </div>
                  )}

                  <div className="mb-3">
                    <h6>
                      Evento:{" "}
                      {decodeSpecialCharacters(
                        selectedEvent?.title ||
                          selectedEvent?.summary ||
                          "Evento sin título"
                      )}
                    </h6>
                    <small className="text-muted">
                      Generar reportes de asistencia para este evento
                    </small>
                  </div>

                  {error && error.includes("invitados") ? (
                    <Alert variant="warning" className="mb-3">
                      <h6>No se puede generar reporte, este evento no tiene invitados registrados o validados. </h6>
                    </Alert>
                  ) : (
                    <div>
                      <h6>Visualización</h6>
                      <div className="d-flex gap-2 mb-3">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => onViewEventReport("html")}
                          disabled={loadingReports}
                        >
                          <FontAwesomeIcon icon={faEye} className="me-1" />
                          Ver Informe
                        </Button>
                      </div>

                      <h6>Descargar</h6>
                      <div className="d-flex gap-2 mb-3">
                        <Button
                          size="sm"
                          onClick={() => {
                            setDownloadFormat("xlsx");
                            setShowDownloadModal(true);
                          }}
                          disabled={loadingReports}
                          style={{
                            backgroundColor: "#217346",
                            borderColor: "#217346",
                            color: "white",
                          }}
                        >
                          <FontAwesomeIcon icon={faFile} className="me-1" />
                          Excel
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            setDownloadFormat("pdf");
                            setShowDownloadModal(true);
                          }}
                          disabled={loadingReports}
                        >
                          <FontAwesomeIcon icon={faFileAlt} className="me-1" />
                          PDF
                        </Button>
                      </div>
                    </div>
                  )}

                  {error && !error.includes("invitados") && (
                    <Alert variant="danger" className="mt-3">
                      {error}
                    </Alert>
                  )}

                  {htmlContent && (
                    <div className="mt-3">
                      <h6>Informe:</h6>
                      <iframe
                        srcDoc={htmlContent}
                        style={{
                          width: "100%",
                          height: "400px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                        }}
                        title="Reporte HTML"
                      />
                    </div>
                  )}
                </div>
              </Tab>
            )}
          </Tabs>
        )}
        
        {/* Modal de Confirmación de Descarga */}
        <Modal show={showDownloadModal} onHide={() => setShowDownloadModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Tipo de Reporte</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>¿Qué tipo de reporte deseas descargar?</p>
            <div className="d-grid gap-2">
              <Button 
                variant="outline-primary" 
                onClick={() => confirmDownload(true)}
                className="text-start"
              >
                <strong>Solo Asistentes</strong>
                <br />
                <small className="text-muted">Incluye únicamente las personas que confirmaron asistencia</small>
              </Button>
              <Button 
                variant="outline-primary" 
                onClick={() => confirmDownload(false)}
                className="text-start"
              >
                <strong>Todos los Invitados</strong>
                <br />
                <small className="text-muted">Incluye todos los invitados (asistieron o no)</small>
              </Button>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDownloadModal(false)}>
              Cancelar
            </Button>
          </Modal.Footer>
        </Modal>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          style={{
            backgroundColor: "#C5292B",
            borderColor: "#C5292B",
            color: "white",
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#A02225";
            e.target.style.borderColor = "#A02225";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "#C5292B";
            e.target.style.borderColor = "#C5292B";
          }}
        >
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EventDetailModal;
